import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { LogOut, RefreshCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = 'yv-admin';
const SYNC = { not_connected: 'Stored (CRM Not Connected)', pending: 'Pending', sent: 'Sent To CRM', failed: 'Failed — Retry' };
const errText = (e) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : 'Something went wrong. Please try again.'; };

const Login = ({ onIn }) => {
  const [f, setF] = useState({ email: '', password: '' });
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault(); setErr('');
    try { const { data } = await axios.post(`${API}/auth/login`, f); sessionStorage.setItem(KEY, data.token); onIn(data.token); } catch (x) { setErr(errText(x)); }
  };
  return (
    <form onSubmit={submit} className="yv-form yv-admin-login" data-testid="admin-login-form">
      <h1 className="yv-h3">Administrator Sign In</h1>
      <input className="yv-input" type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-email" />
      <input className="yv-input" type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-password" />
      {err && <p className="yv-field-err" data-testid="admin-login-error">{err}</p>}
      <button className="yv-btn yv-btn-primary yv-btn-block" data-testid="admin-login-btn">Sign In</button>
    </form>
  );
};

const Stat = ({ id, label, value }) => <div className="yv-admin-stat" data-testid={`admin-stat-${id}`}><span className="yv-mono">{label}</span><b className="yv-keep-case">{value}</b></div>;
const list = (o) => Object.entries(o || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—';

const Dashboard = ({ token, onOut }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([axios.get(`${API}/admin/stats`, h), axios.get(`${API}/admin/applications`, h)]);
      setData({ stats: s.data, apps: a.data.applications });
    } catch (x) { if (x.response?.status === 401) onOut(); else setErr(errText(x)); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const resync = async (id) => { try { await axios.post(`${API}/admin/applications/${id}/resync`, {}, h); load(); } catch (x) { setErr(errText(x)); } };
  if (!data) return <p className="yv-lede" data-testid="admin-loading">{err || 'Loading…'}</p>;
  const { stats, apps } = data;
  const connected = stats.integration.mode !== 'not_connected';
  return (
    <div data-testid="admin-dashboard">
      <div className={`yv-admin-int ${connected ? 'is-on' : ''}`} data-testid="admin-integration-status">
        GoHighLevel: {connected ? `Connected (${stats.integration.mode})` : 'Not Connected — Applications Are Stored Securely Here Until It Is Connected'} · Pipeline: {stats.integration.pipeline}
      </div>
      <div className="yv-admin-stats">
        <Stat id="total" label="Applications" value={stats.total} />
        <Stat id="scheduled" label="Discovery Calls Scheduled" value={stats.scheduled} />
        <Stat id="sources" label="Lead Sources" value={list(stats.bySource)} />
        <Stat id="solutions" label="Solutions Requested" value={list(stats.bySolution)} />
      </div>
      <div className="yv-cmp-wrap">
        <table className="yv-cmp yv-admin-table yv-keep-case" data-testid="admin-applications-table">
          <thead><tr>{['Date', 'Name', 'Company', 'Contact', 'Industry', 'Solution', 'Revenue / Leads / Budget', 'Challenge', 'Source', 'Call', 'CRM Sync'].map((x) => <th key={x}>{x}</th>)}</tr></thead>
          <tbody>
            {apps.map((a) => (
              <tr key={a.id} data-testid={`admin-row-${a.id}`}>
                <td>{new Date(a.created_at).toLocaleString()}</td><td>{a.fullName}</td><td>{a.companyName}{a.website && <><br /><span className="yv-keep-case">{a.website}</span></>}</td>
                <td className="yv-keep-case">{a.email}<br />{a.phone}{a.consentSms ? ' · SMS OK' : ''}</td><td>{a.industry}</td><td>{a.solution}</td>
                <td>{[a.monthlyRevenue, a.monthlyLeadVolume, a.budget].map((v) => v || '—').join(' / ')}</td><td className="yv-admin-challenge">{a.challenge}</td>
                <td>{a.source?.utm_source || a.source?.referrer || 'Direct'}</td><td>{a.scheduled ? 'Scheduled' : '—'}</td>
                <td>{SYNC[a.integration?.status] || a.integration?.status}{connected && a.integration?.status !== 'sent' && <button className="yv-hd-btn" onClick={() => resync(a.id)} data-testid={`admin-resync-${a.id}`}><RefreshCcw className="w-3.5 h-3.5" />Retry</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {err && <p className="yv-field-err">{err}</p>}
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(KEY));
  const out = () => { sessionStorage.removeItem(KEY); setToken(null); };
  useEffect(() => { document.title = 'Admin | YOVAI'; }, []);
  return (
    <div className="landing-page yv-home yv-page" data-testid="admin-page">
      <meta name="robots" content="noindex" />
      <header className="yv-nav is-scrolled"><div className="yv-container yv-nav-inner">
        <Link to="/" className="yv-nav-logo"><img src="/yovai-logo-light.png" alt="YOVAI" /></Link>
        {token && <button onClick={out} className="yv-nav-contact" style={{ display: 'inline-flex' }} data-testid="admin-logout-btn"><LogOut className="w-4 h-4 mr-2" />Sign Out</button>}
      </div></header>
      <main className="yv-section yv-dark"><div className="yv-container">{token ? <Dashboard token={token} onOut={out} /> : <Login onIn={setToken} />}</div></main>
    </div>
  );
}
